/**
 * Colour representation shared by Garaimon art and gathered pixels.
 *
 * The mini-game has to compare two kinds of colour data: the colours of a
 * creature's art, and the colours of the pixels a player gathers. Comparing them
 * only makes sense if both are described the same way, so everything goes
 * through one function here, `encodeColour`.
 *
 * The rule that matters most: a colour is encoded to exactly ONE anchor, the
 * nearest in Oklab. A purple pixel therefore counts as purple. It is never split
 * across a red anchor and a blue anchor, which is what would happen if the
 * generation-time mix of colours were counted directly.
 *
 * Oklab is used throughout because it is a colour space where equal distances
 * look like equal differences to the eye, so "these two shades are nearly the
 * same" is a measurement rather than a guess.
 */

import { COLOUR_ANCHORS, type ColourAnchor } from './colourAnchors.generated';
import { COLLECT_CONFIG } from './collectConfig';

export type Rgb = readonly [number, number, number];
export type Oklab = readonly [number, number, number];

/**
 * A colour mixture: how much mass sits on each anchor.
 *
 * `mass` is indexed in the same order as `COLOUR_ANCHORS`. Raw counts and
 * normalised proportions are the same shape, so nothing needs converting except
 * the numbers themselves. Comparisons use proportions, so gathering more pixels
 * of the same mixture does not change how well it matches.
 */
export interface ColourMixture {
  readonly mass: readonly number[];
  readonly total: number;
}

const ANCHOR_COUNT = COLOUR_ANCHORS.length;
const ANCHOR_OKLAB: readonly Oklab[] = COLOUR_ANCHORS.map((anchor) => anchor.oklab);

function srgbChannelToLinear(value: number): number {
  return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
}

function linearChannelToSrgb(value: number): number {
  return value <= 0.0031308 ? value * 12.92 : 1.055 * value ** (1 / 2.4) - 0.055;
}

export function srgbToOklab(rgb: Rgb): Oklab {
  const r = srgbChannelToLinear(rgb[0] / 255);
  const g = srgbChannelToLinear(rgb[1] / 255);
  const b = srgbChannelToLinear(rgb[2] / 255);
  const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
  const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
  const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;
  const l_ = Math.cbrt(l);
  const m_ = Math.cbrt(m);
  const s_ = Math.cbrt(s);
  return [
    0.2104542553 * l_ + 0.7936177850 * m_ - 0.0040720468 * s_,
    1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_,
    0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_,
  ];
}

export function oklabToRgb(lab: Oklab): Rgb {
  const l_ = lab[0] + 0.3963377774 * lab[1] + 0.2158037573 * lab[2];
  const m_ = lab[0] - 0.1055613458 * lab[1] - 0.0638541728 * lab[2];
  const s_ = lab[0] - 0.0894841775 * lab[1] - 1.2914855480 * lab[2];
  const l = l_ ** 3;
  const m = m_ ** 3;
  const s = s_ ** 3;
  const r = 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
  const g = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
  const b = -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s;
  const toByte = (value: number) => Math.max(0, Math.min(255, Math.round(linearChannelToSrgb(value) * 255)));
  return [toByte(r), toByte(g), toByte(b)];
}

export function oklabDistance(a: Oklab, b: Oklab): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
}

/**
 * The single anchor a colour is represented by: the nearest in Oklab.
 *
 * A hard nearest-anchor choice is deliberate. Soft, distance-weighted encoding
 * would spread one displayed colour across several anchors, which is exactly the
 * double-counting this design forbids. Because the anchors were merged in Oklab
 * before this ran, two shades that look the same land on the same anchor.
 */
export function encodeColourIndex(rgb: Rgb): number {
  const lab = srgbToOklab(rgb);
  let bestIndex = 0;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (let index = 0; index < ANCHOR_COUNT; index += 1) {
    const distance = oklabDistance(lab, ANCHOR_OKLAB[index]!);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIndex = index;
    }
  }
  return bestIndex;
}

export function encodeColour(rgb: Rgb): ColourAnchor {
  return COLOUR_ANCHORS[encodeColourIndex(rgb)]!;
}

export function emptyMixture(): ColourMixture {
  return { mass: new Array<number>(ANCHOR_COUNT).fill(0), total: 0 };
}

const ANCHOR_INDEX_BY_ID = new Map(COLOUR_ANCHORS.map((anchor, index) => [anchor.id, index] as const));

/** The position of an anchor in the palette, looked up by its stable id. */
export function anchorIndexById(id: string): number {
  const index = ANCHOR_INDEX_BY_ID.get(id);
  if (index === undefined) throw new Error(`Garaimon: there is no colour anchor with id "${id}".`);
  return index;
}

/**
 * A mixture from stored profile shares, such as those in
 * `colourProfiles.generated.ts`. Shares are proportions, and only their ratio
 * matters for comparison, so they are placed directly as mass.
 */
export function mixtureFromShares(shares: ReadonlyArray<{ anchor: string; share: number }>): ColourMixture {
  const accumulation = { mass: new Array<number>(ANCHOR_COUNT).fill(0), total: 0 };
  for (const entry of shares) {
    if (!Number.isFinite(entry.share) || entry.share < 0) throw new Error(`Garaimon: anchor "${entry.anchor}" has an invalid share of ${entry.share}.`);
    if (entry.share === 0) continue;
    addEncoded(accumulation, anchorIndexById(entry.anchor), entry.share);
  }
  return accumulation;
}

/** Adds one already-encoded pixel to a mixture. */
export function addEncoded(mixture: { mass: number[]; total: number }, anchorIndex: number, amount = 1): void {
  if (anchorIndex < 0 || anchorIndex >= ANCHOR_COUNT) throw new Error(`Garaimon: anchor index ${anchorIndex} is outside the palette.`);
  mixture.mass[anchorIndex] = (mixture.mass[anchorIndex] ?? 0) + amount;
  mixture.total += amount;
}

/** Builds a mixture from raw pixel colours, encoding each through `encodeColour`. */
export function mixtureFromColours(colours: Iterable<Rgb>): ColourMixture {
  const accumulation = { mass: new Array<number>(ANCHOR_COUNT).fill(0), total: 0 };
  for (const colour of colours) addEncoded(accumulation, encodeColourIndex(colour));
  return accumulation;
}

/** A mixture from a caller that already knows the anchor counts. */
export function mixtureFromCounts(counts: ReadonlyMap<number, number>): ColourMixture {
  const accumulation = { mass: new Array<number>(ANCHOR_COUNT).fill(0), total: 0 };
  for (const [anchorIndex, count] of counts) addEncoded(accumulation, anchorIndex, count);
  return accumulation;
}

export function normaliseMixture(mixture: ColourMixture): number[] {
  const result = new Array<number>(ANCHOR_COUNT).fill(0);
  if (mixture.total <= 0) return result;
  for (let index = 0; index < ANCHOR_COUNT; index += 1) result[index] = (mixture.mass[index] ?? 0) / mixture.total;
  return result;
}

/**
 * Projection directions used by the sliced-Wasserstein distance.
 *
 * The three Oklab axes are included explicitly so an obvious difference along a
 * principal axis can never be missed, and the rest are spread over the sphere by
 * a Fibonacci spiral for even coverage. A direction and its opposite give the
 * same answer, so the effective count is about half of this.
 */
function buildDirections(): Oklab[] {
  const directions: Oklab[] = [
    [1, 0, 0], [-1, 0, 0],
    [0, 1, 0], [0, -1, 0],
    [0, 0, 1], [0, 0, -1],
  ];
  const count = 32;
  const golden = Math.PI * (1 + Math.sqrt(5));
  for (let index = 0; index < count; index += 1) {
    const y = 1 - (2 * (index + 0.5)) / count;
    const radius = Math.sqrt(Math.max(0, 1 - y * y));
    const theta = golden * index;
    directions.push([Math.cos(theta) * radius, y, Math.sin(theta) * radius]);
  }
  return directions;
}

interface Slice {
  /** Anchor indices ordered by projection along this direction. */
  readonly order: readonly number[];
  /** Projected positions, in the same order as `order`. */
  readonly positions: readonly number[];
  /** Spread of the projections, used to normalise this slice to 0..1. */
  readonly range: number;
}

function buildSlices(directions: readonly Oklab[]): Slice[] {
  return directions.map((direction) => {
    const order = ANCHOR_OKLAB.map((_anchor, index) => index).sort(
      (a, b) =>
        direction[0] * ANCHOR_OKLAB[a]![0] + direction[1] * ANCHOR_OKLAB[a]![1] + direction[2] * ANCHOR_OKLAB[a]![2] -
        (direction[0] * ANCHOR_OKLAB[b]![0] + direction[1] * ANCHOR_OKLAB[b]![1] + direction[2] * ANCHOR_OKLAB[b]![2]),
    );
    const positions = order.map(
      (index) => direction[0] * ANCHOR_OKLAB[index]![0] + direction[1] * ANCHOR_OKLAB[index]![1] + direction[2] * ANCHOR_OKLAB[index]![2],
    );
    const range = positions.length ? positions[positions.length - 1]! - positions[0]! : 0;
    return { order, positions, range };
  });
}

const SLICES: readonly Slice[] = buildSlices(buildDirections());

/**
 * Sliced-Wasserstein distance between two colour mixtures, in 0..1.
 *
 * Imagine each mixture as piles of sand sitting at different shade positions.
 * This looks at the piles from many fixed directions, and for each one measures
 * how far the sand must move to make the two profiles line up. Averaging those
 * measurements gives one number.
 *
 * Three properties fall out of that, and all three are what the design needs:
 * it is smooth, so there is no arbitrary cut-off; it is scale-free, so gathering
 * more pixels of the same mixture changes nothing; and the cost of substituting
 * one colour for another grows with how different they look, so a near-blue is
 * cheap to swap for blue and orange is expensive.
 */
export function recipeDistance(a: ColourMixture, b: ColourMixture): number {
  if (a.total <= 0 || b.total <= 0) return 1;
  const proportionsA = normaliseMixture(a);
  const proportionsB = normaliseMixture(b);

  let total = 0;
  let slices = 0;
  for (const slice of SLICES) {
    if (slice.range <= 1e-9) continue;
    const { order, positions, range } = slice;
    let cumulativeA = 0;
    let cumulativeB = 0;
    let transport = 0;
    for (let step = 0; step < order.length - 1; step += 1) {
      const anchorIndex = order[step]!;
      cumulativeA += proportionsA[anchorIndex] ?? 0;
      cumulativeB += proportionsB[anchorIndex] ?? 0;
      transport += Math.abs(cumulativeA - cumulativeB) * (positions[step + 1]! - positions[step]!);
    }
    total += transport / range;
    slices += 1;
  }
  return slices === 0 ? 1 : Math.max(0, Math.min(1, total / slices));
}

/**
 * The share of the way from "no relationship at all" to "exactly this profile".
 *
 * The curve is a smooth falloff rather than a hard threshold, so a small
 * deviation barely moves the score and a large one falls away gradually. It
 * never returns a verdict, only a degree of similarity. An empty mixture has no
 * resemblance to anything and scores zero.
 */
export function matchScore(a: ColourMixture, b: ColourMixture, tolerance = COLLECT_CONFIG.matchTolerance): number {
  if (a.total <= 0 || b.total <= 0) return 0;
  const distance = recipeDistance(a, b);
  const ratio = distance / tolerance;
  return Math.max(0, Math.min(1, Math.exp(-ratio * ratio)));
}

/** The anchors that actually carry mass, largest first. Used for hints and readouts. */
export function dominantAnchors(mixture: ColourMixture, limit = 3): Array<{ anchor: ColourAnchor; share: number }> {
  const proportions = normaliseMixture(mixture);
  return proportions
    .map((share, index) => ({ anchor: COLOUR_ANCHORS[index]!, share }))
    .filter((entry) => entry.share > 0)
    .sort((a, b) => b.share - a.share)
    .slice(0, limit);
}

/** Hex for an anchor index, for readouts and tests. */
export function anchorHex(index: number): string {
  const anchor = COLOUR_ANCHORS[index];
  if (!anchor) throw new Error(`Garaimon: anchor index ${index} is outside the palette.`);
  return anchor.hex;
}

/**
 * A word for an anchor's colour, for hints such as "needs more blue".
 *
 * The anchors are generated with opaque ids like `a17`, which say nothing a
 * player could act on, so the name is derived from the anchor's own Oklab
 * position rather than stored. That keeps the names correct when the palette is
 * regenerated, which renumbers and remerges anchors.
 *
 * Hue comes from the a/b plane and lightness from `L`. A colour too grey to have
 * a readable hue is called grey, black or white rather than being forced into the
 * nearest hue, and a very dark or very light colour is qualified so the two ends
 * of a hue are distinguishable - there are four blues in this palette, and
 * "light blue" and "dark blue" are different things to aim for. The names are
 * deliberately coarse: a hint is a direction to move in, not a name for a shade.
 *
 * The thresholds were chosen by naming every anchor in the palette and checking
 * each one reads as the colour a person sees, rather than picked as round
 * numbers. `colour.test.ts` fixes the ones that matter.
 */
export function anchorColourName(index: number): string {
  const anchor = COLOUR_ANCHORS[index];
  if (!anchor) throw new Error(`Garaimon: anchor index ${index} is outside the palette.`);
  const [lightness, a, b] = anchor.oklab;
  // Below this the hue is not reliably readable, so the colour counts as neutral.
  if (Math.hypot(a, b) < NEUTRAL_CHROMA) return lightness < 0.30 ? 'black' : lightness > 0.88 ? 'white' : 'grey';
  // Degrees anticlockwise from the red axis: 0 is red, 90 is yellow, 270 is blue.
  // Each sector starts at the boundary listed, and the last one wraps past 345.
  const hue = ((Math.atan2(b, a) * 180) / Math.PI + 360) % 360;
  const named =
    hue >= 345 || hue < 20 ? 'red'
    : hue < 62 ? 'orange'
    : hue < 105 ? 'yellow'
    : hue < 165 ? 'green'
    : hue < 240 ? 'teal'
    : hue < 295 ? 'blue'
    : 'purple';
  return `${lightness < 0.38 ? 'dark ' : lightness > 0.76 ? 'light ' : ''}${named}`;
}

/** The chroma below which a colour has no hue a player could name. */
const NEUTRAL_CHROMA = 0.05;

/**
 * The anchor nearest to a colour, given as a hex string. Used by tooling and
 * tests to name an anchor by the colour a person would recognise, so a
 * regenerated palette does not break them.
 */
export function anchorIndexNearHex(hex: string): number {
  const value = hex.replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(value)) throw new Error(`Garaimon: "${hex}" is not a six-digit hex colour.`);
  return encodeColourIndex([
    Number.parseInt(value.slice(0, 2), 16),
    Number.parseInt(value.slice(2, 4), 16),
    Number.parseInt(value.slice(4, 6), 16),
  ]);
}

/**
 * The share difference below which a colour is not worth mentioning.
 *
 * A hint is advice, so it should only appear when following it would move the
 * mixture by an amount the distance measure can actually feel. This is a
 * twentieth of the mixture, which is small enough to be worth acting on and
 * large enough not to be an artefact of rounding.
 */
const GAP_HINT_THRESHOLD = 0.05;

/**
 * What a gathered mixture is short of and has too much of, against a target.
 *
 * The answer is grouped by colour name rather than by anchor, because several
 * anchors share a name: telling a player they need "more blue" once is a usable
 * hint, whereas "more blue, more dark blue" is noise. Anchors whose shares are
 * close enough to be indistinguishable are dropped, so a well-matched mixture
 * gets no advice at all rather than advice built from rounding.
 *
 * Positive means the target has more of that colour than was gathered, so
 * "more"; negative means the collection has more than the target, so "less".
 */
export function mixtureGapHints(gathered: ColourMixture, target: ColourMixture, limit = 2): string[] {
  if (gathered.total <= 0 || target.total <= 0) return [];
  const have = normaliseMixture(gathered);
  const want = normaliseMixture(target);
  const byName = new Map<string, number>();
  for (let index = 0; index < ANCHOR_COUNT; index += 1) {
    const difference = (want[index] ?? 0) - (have[index] ?? 0);
    if (Math.abs(difference) < GAP_HINT_THRESHOLD) continue;
    const name = anchorColourName(index);
    byName.set(name, (byName.get(name) ?? 0) + difference);
  }
  return [...byName.entries()]
    .filter(([, difference]) => Math.abs(difference) >= GAP_HINT_THRESHOLD)
    .sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]))
    .slice(0, limit)
    .map(([name, difference]) => `${difference > 0 ? 'more' : 'less'} ${name}`);
}

export { COLOUR_ANCHORS, ANCHOR_COUNT };
export type { ColourAnchor };